import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { AiIntegrationsService, AiWallet } from './ai-integrations.service';
import { AuthService } from './auth.service';

/**
 * Shared state for the user's AI credit wallet.
 * Used by the topbar indicator, the AI dashboard and any component
 * that consumes credits, so the balance stays in sync everywhere
 * with a smooth visual countdown effect.
 */
@Injectable({ providedIn: 'root' })
export class AiWalletStateService {
  private _balance = new BehaviorSubject<number | null>(null);
  private _reserved = new BehaviorSubject<number>(0);
  private _total = new BehaviorSubject<number>(0);
  private _hasWallet = new BehaviorSubject<boolean>(false);
  private _pulse = new Subject<{ credits: number }>();

  balance$ = this._balance.asObservable();
  reserved$ = this._reserved.asObservable();
  total$ = this._total.asObservable();
  hasWallet$ = this._hasWallet.asObservable();
  pulse$ = this._pulse.asObservable();

  constructor(
    private svc: AiIntegrationsService,
    private authService: AuthService,
  ) {}

  get balance(): number | null { return this._balance.value; }

  /** Clears all cached values (used on logout / role change). */
  reset(): void {
    this._hasWallet.next(false);
    this._balance.next(null);
    this._reserved.next(0);
    this._total.next(0);
  }

  refresh(): void {
    this.svc.getWallets().subscribe({
      next: (wallets) => {
        if (!wallets || wallets.length === 0) {
          this.reset();
          return;
        }

        const currentUser = this.authService.currentUserValue;
        const roleName = String(currentUser?.role?.name ?? '').toUpperCase();

        let total = 0;
        let reserved = 0;

        if (roleName === 'MASTER') {
          const masterWallet =
            wallets.find((wallet) => wallet.scope === 'ENTITY') ?? wallets[0];
          total = masterWallet?.balance ?? 0;
          reserved = masterWallet?.reservedBalance ?? 0;
        } else {
          total = wallets.reduce((sum, wallet) => sum + (wallet.balance ?? 0), 0);
          reserved = wallets.reduce(
            (sum, wallet) => sum + (wallet.reservedBalance ?? 0),
            0,
          );
        }

        this._hasWallet.next(true);
        this._total.next(total);
        this._reserved.next(reserved);
        this._balance.next(Math.max(0, total - reserved));
      },
      error: () => this.reset(),
    });
  }

  setFromWallet(w: AiWallet): void {
    this._hasWallet.next(true);
    this._total.next(w.balance);
    this._reserved.next(w.reservedBalance);
    this._balance.next(w.balance - w.reservedBalance);
  }

  /** Set new available balance (post AI usage). Triggers a pulse for animation. */
  applyConsumption(remainingBalance: number, credits: number): void {
    if (this._balance.value != null) {
      this._balance.next(Math.max(0, this._balance.value - credits));
      this._total.next(Math.max(0, this._total.value - credits));
    } else if (typeof remainingBalance === 'number') {
      this._balance.next(Math.max(0, remainingBalance));
    }
    this._pulse.next({ credits });
  }
}
